import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getPEOReportMetadata,
  executePEOReport,
  exportPEOReportEmails,
} from '../../endpoints/peoReportsTs';
import {
  ReportMetadata,
  ExecuteReportRequest,
  ExecuteReportResponse,
} from '../../types/peoReports';
import ClientSideTable, {
  ClientSideTableColumn,
} from '../../Components/ClientSideTable';
import ExportButton from '../../Components/ExportButton';
import ChapterSelector from '../../Components/ChapterSelector';
import SPDSelector from '../../Components/SPDSelector';
import MemberActiveFilter from '../../Components/MemberActiveFilter';
import LocalChapterRolesFilter from '../../Components/LocalChapterRolesFilter';

const PEOReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [bannerDismissed, setBannerDismissed] = useState(() => {
    return localStorage.getItem('peo-reports-banner-dismissed') === 'true';
  });

  const [filterValues, setFilterValues] = useState<ExecuteReportRequest>({});
  const [reportData, setReportData] = useState<ExecuteReportResponse | null>(
    null
  );
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState<string>('');
  const [emailCount, setEmailCount] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >({});

  const {
    data: metadata,
    isLoading,
    error,
  } = useApiGet<ReportMetadata>(
    ['peo-report-metadata', id],
    () => getPEOReportMetadata(id!),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  const hasEmailColumn =
    metadata?.columns?.some((col) => col.type === 'email') || false;

  // Initialize column visibility from metadata
  useEffect(() => {
    if (metadata?.columns) {
      const initialVisibility: Record<string, boolean> = {};
      metadata.columns.forEach((col) => {
        initialVisibility[col.field] = col.visible;
      });
      setColumnVisibilityModel(initialVisibility);
    }
  }, [metadata]);

  const handleDismissBanner = () => {
    localStorage.setItem('peo-reports-banner-dismissed', 'true');
    setBannerDismissed(true);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const isRequiredFieldMissing = () => {
    if (!metadata) return true;
    return metadata.parameters.some((param) => {
      if (!param.required) return false;
      const value = filterValues[param.name as keyof ExecuteReportRequest];
      return !value || value === '';
    });
  };

  const handleExecuteReport = async () => {
    if (!id) return;

    setIsExecuting(true);
    setExecutionError(null);

    try {
      const response = await executePEOReport(id, {
        ...filterValues,
        format: 'json',
      });
      setReportData(response);
    } catch (err: any) {
      console.error('Report execution failed:', err);
      setExecutionError(
        err.message || 'Failed to execute report. Please try again.'
      );
    } finally {
      setIsExecuting(false);
    }
  };

  const handleEmailExport = async () => {
    if (!id) return;

    try {
      const response = await exportPEOReportEmails(id, filterValues);
      setEmailData(response.emails);
      setEmailCount(response.count);
      setEmailDialogOpen(true);
    } catch (err: any) {
      console.error('Email export failed:', err);
      setSnackbarMessage('Failed to export emails. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleCopyEmails = () => {
    navigator.clipboard.writeText(emailData);
    setSnackbarMessage('Email addresses copied to clipboard!');
    setSnackbarOpen(true);
  };

  const formatDateForAPI = (date: Dayjs | null): string => {
    if (!date) return '';
    return date.format('YYYY-MM-DD');
  };

  // Map report columns to ClientSideTable format
  const tableColumns: ClientSideTableColumn<any>[] =
    metadata?.columns.map((col) => ({
      id: col.field,
      label: col.label,
      width: `${col.width}px`,
      sortable: col.sortable,
    })) || [];

  // Prepare export columns (only include visible columns)
  const exportColumns =
    metadata?.columns
      .filter((col) => columnVisibilityModel[col.field] !== false)
      .map((col) => ({
        field: col.field,
        headerName: col.label,
        width: col.width,
      })) || [];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !metadata) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error
            ? 'Failed to load report metadata. Please try again.'
            : 'Report not found.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/peo-reports')}
        >
          Back to Reports
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, pb: 8 }}>
        {/* Banner for old reports link */}
        {!bannerDismissed && (
          <Alert
            severity="info"
            icon={<AssessmentIcon />}
            action={
              <IconButton
                size="small"
                onClick={handleDismissBanner}
                aria-label="close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ mb: 3 }}
          >
            Looking for the previous reports system?{' '}
            <RouterLink
              to="/reports"
              style={{
                color: 'inherit',
                fontWeight: 600,
                textDecoration: 'underline',
              }}
            >
              Use previous Hub Reports
            </RouterLink>
          </Alert>
        )}

        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/peo-reports')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {metadata.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {metadata.description}
            </Typography>
          </Box>
        </Stack>

        {/* Filters Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Report Parameters
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {metadata.parameters.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                This report does not require any parameters.
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {metadata.parameters.map((param) => {
                  const isRequired = param.required;
                  const label = isRequired
                    ? `${param.description} *`
                    : param.description;

                  if (param.name === 'local_chapter_id') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <ChapterSelector
                          chapter={filterValues.local_chapter_id || ''}
                          spd={filterValues.spd_chapter_id || null}
                          reportId={id!}
                          handleChapterSelect={(value: string | any | null) =>
                            handleFilterChange('local_chapter_id', value || '')
                          }
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'spd_chapter_id') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <SPDSelector
                          spd={filterValues.spd_chapter_id || ''}
                          reportId={id!}
                          handleSpdSelect={(value: string | any | null) =>
                            handleFilterChange('spd_chapter_id', value || '')
                          }
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'status') {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <MemberActiveFilter
                          filterValue={filterValues.status || ''}
                          updateFilters={(value: string) =>
                            handleFilterChange('status', value)
                          }
                          disabled={false}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'local_chapter_roles') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <LocalChapterRolesFilter
                          value={filterValues.local_chapter_roles || ''}
                          onChange={(value: string) =>
                            handleFilterChange('local_chapter_roles', value)
                          }
                          disabled={false}
                          required={param.required}
                        />
                      </Grid>
                    );
                  }

                  if (
                    param.type === 'date' &&
                    (param.name === 'as_of_date' ||
                      param.name === 'start_date' ||
                      param.name === 'end_date')
                  ) {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <DatePicker
                          label={label}
                          value={
                            filterValues[param.name]
                              ? dayjs(filterValues[param.name])
                              : null
                          }
                          onChange={(newValue) =>
                            handleFilterChange(
                              param.name,
                              formatDateForAPI(newValue)
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              required: isRequired,
                            },
                          }}
                        />
                      </Grid>
                    );
                  }

                  return null;
                })}
              </Grid>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handleExecuteReport}
                disabled={isExecuting || isRequiredFieldMissing()}
              >
                {isExecuting ? 'Running Report...' : 'Run Report'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Execution Error */}
        {executionError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setExecutionError(null)}
          >
            {executionError}
          </Alert>
        )}

        {/* Results Card */}
        {reportData && (
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Report Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {reportData.row_count} record
                    {reportData.row_count !== 1 ? 's' : ''} • Executed in{' '}
                    {reportData.execution_time}ms
                  </Typography>
                </Box>
                <ExportButton
                  data={reportData.data || []}
                  columns={exportColumns}
                  filename={`${metadata.name.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}`}
                  hasEmailColumn={hasEmailColumn}
                  onEmailExport={handleEmailExport}
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {!reportData.data || reportData.data.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No data found for the selected parameters.
                  </Typography>
                </Box>
              ) : (
                <ClientSideTable
                  data={reportData.data}
                  columns={tableColumns}
                  enableColumnVisibility={true}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityChange={setColumnVisibilityModel}
                  initialRowsPerPage={25}
                  rowsPerPageOptions={[10, 25, 50, 100, 250]}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Email Export Dialog */}
        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Email List Export
            <IconButton
              onClick={() => setEmailDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {emailCount} unique email address{emailCount !== 1 ? 'es' : ''}{' '}
              found. Emails are separated by semicolons for easy copy/paste into
              email clients.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={emailData}
              InputProps={{
                readOnly: true,
              }}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyEmails}
            >
              Copy to Clipboard
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default PEOReportDetail;
